import PropTypes from 'prop-types'
import DtoAnalysisLight from 'quality/dto/analyse/DtoAnalysisLight'
import { groupBy, round, max, orderBy, isEmpty, minBy, maxBy, meanBy, isUndefined, countBy, floor, compact, isNaN, intersectionWith, keys, sumBy, uniq } from 'lodash'
import i18n from 'simple-react-i18n'
import ReactECharts from 'echarts-for-react'
import echarts from 'echarts/lib/echarts'
import React, { useState, useMemo, useEffect } from 'react'
import { getBeginingOfTheYear, getEndOfTheYear, getMonthList } from 'utils/DateUtil'
import moment from 'moment'
import { chartSymbol, filterIcon, fullScreenExitIcon, fullScreenIcon, quantificationIcon, swapVertical } from 'components/echart/EChartUtils'
import { exportPictureIcon, exportExcelIcon } from 'components/echart/EChartUtils'
import { exportFile } from 'utils/ExportDataUtil'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { grey, greyBlue, mainBlue, THRESHOLD_COLORS_CODES } from 'utils/constants/ColorTheme'
import useBoolean from 'utils/customHook/useBoolean'
import { Button, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import NumberField from 'components/forms/NumberField'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import { getMonth } from 'date-fns'
import SelectionTableModal from 'components/modal/SelectionTableModal'
import { normalizeString } from 'utils/StringUtil'
import { formatParameterSelectedPopup } from 'campaign/utils/CampaignUtils'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting, { listStringParser } from 'utils/customHook/useApplicationSetting'
import AnalysisAction from 'quality/actions/AnalysisAction'
import { hasValue } from 'utils/NumberUtil'
import useAbortController from 'utils/customHook/useAbortController'
import ProgressCard from 'components/card/ProgressCard'
import { ParameterFilterField } from 'components/datatable/SimpleSelectionTable'
import { REMARK_CODES } from 'utils/AnalyseUtils'
import { CATCHMENT_IRRELEVANT_PESTICIDES, CATCHMENT_LIST_SUM_PESTICIDES, CATCHMENT_PESTICIDES } from 'catchment/constants/CatchmentConstant'

const LOCAL_STORAGE_ORDINATES = 'ANNUAL_DISTRIBUTION_CHART_ORDINATES'
const LOCAL_STORAGE_PESTICIDES = 'ANNUAL_DISTRIBUTION_CHART_PESTICIDES'
const GHOST_SERIE = 'GHOST'
const EXPORT_CATCHMENT_COLUMNS = ['code', 'catchment', 'month', 'min', 'max', 'average', 'median', 'nbSumPesticides']
const EXPORT_RESOURCE_COLUMNS = ['month', 'min', 'max', 'average', 'median', 'nbSumPesticides']

const parameterFilterFunction = (list, { selection = '-1', listParam, searchValue }) => {
    const filterSelection = selection !== '-1' ? intersectionWith(list, listParam, (elem, id) => elem.id === id) : list
    const searchValueFormat = normalizeString(searchValue)
    return searchValue ? filterSelection.filter(p => p.labelSearch.includes(searchValueFormat)) : filterSelection
}

const calculateMonthMedian = (monthlySerie) => {
    const monthValues = monthlySerie?.map(v => v.result)
    if (isUndefined(monthValues) || monthValues?.length === 0) return undefined
    monthValues.sort((a, b) => a - b)
    const halfMonthlySerie = floor(monthValues.length / 2)
    return monthValues.length % 2 !== 0 ?
        monthValues[halfMonthlySerie] :
        round((monthValues[halfMonthlySerie - 1] + monthValues[halfMonthlySerie]) / 2, 3)
}

const AnnualDistributionGraph = ({
    sumPesticidesAnalysis = [],
    stationIds = [],
    startPeriod = getBeginingOfTheYear(moment().year()),
    endPeriod = getEndOfTheYear(moment().year()),
    producerExcluded = [],
}) => {
    const dispatch = useDispatch()

    const {
        parameters,
        catchment,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        catchment: store.CatchmentReducer.catchment,
    }), shallowEqual)

    const {
        value: featuresVisibility,
        setTrue: showFeatures,
        setFalse: hideFeatures,
    } = useBoolean(false)
    const {
        value: fixMinScale,
        toggle: toggleFixMinScale,
    } = useBoolean(false)
    const {
        value: isLoaded,
        setTrue: loaded,
        setFalse: notLoaded,
    } = useBoolean(false)
    const {
        value: additionnalSerie,
        toggle: toggleAdditionnalSerie,
    } = useBoolean(true)
    const {
        value: isPesticidePopinOpen,
        setTrue: openPesticidesPopin,
        setFalse: closePesticidesPopin,
    } = useBoolean(false)
    const {
        value: isFullScreen,
        setTrue: openFullScreen,
        setFalse: closeFullScreen,
    } = useBoolean(false)
    const {
        value: isOrdinatePopinOpen,
        toggle: toggleOrdinatePopin,
        setFalse: closeOrdinatePopin,
    } = useBoolean(false)

    const {
        controllerRef,
        initController,
    } = useAbortController()

    const [ordinateValues, setOrdinateValues] = useLocalStorage(LOCAL_STORAGE_ORDINATES, { min: null, max: null })
    const [ordinateFields, setOrdinateFields] = useState(ordinateValues)

    const [personalAnalysis, setPersonalAnalysis] = useState([])
    const [progress, setProgress] = useState(0)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const relevantPesticides = useApplicationSetting(CATCHMENT_PESTICIDES, listStringParser)
    const irrelevantPesticides = useApplicationSetting(CATCHMENT_IRRELEVANT_PESTICIDES, listStringParser)
    const pesticides = useMemo(() => uniq([...relevantPesticides, ...irrelevantPesticides]), [relevantPesticides, irrelevantPesticides])
    const listSumPesticides = useApplicationSetting(CATCHMENT_LIST_SUM_PESTICIDES, listStringParser)
    const usedSumPesticides = useMemo(() => listSumPesticides.length ? listSumPesticides : pesticides, [listSumPesticides, pesticides])

    const [consideredPesticides, setConsideredPesticides] = useLocalStorage(LOCAL_STORAGE_PESTICIDES, usedSumPesticides)
    const activeParameters = useMemo(() => formatParameterSelectedPopup(parameters.filter(p => p.active), showSandreCode), [parameters, showSandreCode])

    useEffect(() => {
        const personalPesticides = consideredPesticides.filter(p => !usedSumPesticides.includes(p))
        if (personalPesticides.length === 0) {
            setPersonalAnalysis([])
            setProgress(100)
            loaded()
            return
        }
        notLoaded()
        initController()
        const filter = {
            lightMode: true,
            stations: stationIds,
            startDate: startPeriod,
            endDate: endPeriod,
            excludedProducers: producerExcluded,
            parameters: personalPesticides,
            support: '3',
            quantificationControl: true,
        }
        dispatch(AnalysisAction.getAllAnalysisSeq(
            filter,
            controllerRef.current.signal,
            p => setProgress(p),
            json => {
                loaded()
                setPersonalAnalysis(json.map(a => new DtoAnalysisLight(a)))
            },
        ))
    }, [stationIds, consideredPesticides])

    const commonPesticides = useMemo(() => consideredPesticides.filter(p => usedSumPesticides.includes(p)), [consideredPesticides, usedSumPesticides])
    const commonAnalysis = useMemo(() => sumPesticidesAnalysis.filter(a => commonPesticides.includes(a.parameter)), [sumPesticidesAnalysis, commonPesticides])

    const periodAnalysis = useMemo(() => [...commonAnalysis, ...personalAnalysis]
        .filter(({ sampleDate, result, remark }) => hasValue(result) && remark === REMARK_CODES.VALID && sampleDate >= startPeriod && sampleDate <= endPeriod)
        .sort((a, b) => a.sampleDate - b.sampleDate),
    [personalAnalysis, commonAnalysis, startPeriod, endPeriod])

    const sumAnalysis = useMemo(() => {
        const groupByOperation = groupBy(periodAnalysis, a => `${a.qualitometer}#${a.operation}`)
        return keys(groupByOperation).map(key => {
            const { [key]: randomName = [] } = groupByOperation
            return {
                result: round(sumBy(randomName, 'result'), 3),
                sampleDate: randomName[0].sampleDate,
                qualitometer: randomName[0].qualitometer,
                parameter: randomName[0].parameter,
            }
        })
    }, [periodAnalysis])

    const monthsList = useMemo(() => getMonthList(), [])
    const monthsLabel = useMemo(() => monthsList.map(m => m.name), [monthsList])

    const monthlyAnalysis = useMemo(() => sumAnalysis.map(a => ({ result: a.result, month: monthsList.find(m => m.value === getMonth(a.sampleDate) + 1)?.name || 'noMonth' })), [sumAnalysis, monthsList])
    const groupMonthsAnalysis = useMemo(() => groupBy(monthlyAnalysis, 'month'), [monthlyAnalysis])
    const countMonthsAnalysis = useMemo(() => countBy(monthlyAnalysis, 'month'), [monthlyAnalysis])
    const resultMax = useMemo(() => maxBy(monthlyAnalysis, 'result')?.result, [monthlyAnalysis])
    const minimalBarHeight = useMemo(() => max([resultMax, ordinateValues.max]) * 0.005, [resultMax, ordinateValues.max])

    const firstSerie = useMemo(() => monthsLabel.map(month => ([
        month,
        minBy(groupMonthsAnalysis[month], 'result')?.result,
    ])), [monthsLabel, groupMonthsAnalysis])

    const secondSerie = useMemo(() => monthsLabel.map(month => {
        if (!groupMonthsAnalysis[month] || groupMonthsAnalysis[month].length === 0) return [month, undefined, undefined]
        if (groupMonthsAnalysis[month].length === 1) return [month, minimalBarHeight, groupMonthsAnalysis[month][0].result]
        const maxValue = maxBy(groupMonthsAnalysis[month], 'result')?.result
        const seriesDifference = maxValue - (firstSerie.find(m => m[0] === month)?.[1] ?? 0)
        return [
            month,
            seriesDifference >= minimalBarHeight ? seriesDifference : minimalBarHeight,
            maxValue,
        ]
    }), [monthsLabel, groupMonthsAnalysis, firstSerie, minimalBarHeight])

    const averageSerie = useMemo(() => monthsLabel.map(month => ([
        month,
        round(meanBy(groupMonthsAnalysis[month], 'result'), 3),
    ])), [monthsLabel, groupMonthsAnalysis])

    const medianSerie = useMemo(() => monthsLabel.map(month => ([
        month,
        calculateMonthMedian(groupMonthsAnalysis[month]),
    ])), [monthsLabel, groupMonthsAnalysis])

    const additionnalSeries = useMemo(() => additionnalSerie ? [
        {
            type: 'line',
            symbolSize: 6,
            animationDuration: 100,
            name: i18n.average,
            data: averageSerie,
            itemStyle: { color: THRESHOLD_COLORS_CODES.BLACK },
        },
        {
            type: 'line',
            symbolSize: 6,
            animationDuration: 100,
            name: i18n.median,
            data: medianSerie,
            itemStyle: { color: THRESHOLD_COLORS_CODES.RED },

        },
    ] : [undefined],
    [averageSerie, medianSerie, additionnalSerie])

    const series = useMemo(() => compact([
        {
            name: GHOST_SERIE,
            type: 'bar',
            stack: 'stack',
            itemStyle: {
                borderColor: 'transparent',
                color: 'transparent',
            },
            data: firstSerie,
        },
        {
            name: i18n.monthlyQuantifications,
            type: 'bar',
            stack: 'stack',
            data: secondSerie,
            itemStyle: {
                color: mainBlue,
            },
        },
        ...additionnalSeries,

    ]), [firstSerie, secondSerie, additionnalSeries])

    const options = {
        title: {
            text: i18n.annualQuantificationsDistribution,
            left: 'center',
            top: '5%',
        },
        series,
        xAxis: [{
            type: 'category',
            data: monthsLabel,
            axisLabel: {
                interval: 0,
                rotate: 50,
            },
        }],
        yAxis: [{
            type: 'value',
            name: `${i18n.substanceContents} (µg/L)`,
            nameLocation: 'center',
            nameGap: 45 + max([0, round(resultMax, 0).toString().length - 4]) * 10,
            min: fixMinScale ? 0 : ordinateValues.min,
            max: ordinateValues.max,
        }],
        legend: {
            top: '55',
            left: '10%',
            right: featuresVisibility ? 240 : '8%',
            type: 'scroll',
            show: true,
            data: series.map(s => s.name === GHOST_SERIE ? {} : { name: s.name, icon: s.name === i18n.monthlyQuantifications ? 'roundRect' : null }),
        },
        grid: {
            top: '100',
            left: '6%',
            right: '3%',
            bottom: '10',
            containLabel: true,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
                snap: true,
            },
            formatter: params => {
                const barData = params.map(serie => serie.data)
                const additionnalTooltip = additionnalSerie ?
                    `
                    ${i18n.average} : ${isNaN(barData[2][1]) ? '' : barData[2][1]} µg/L<br>
                    ${i18n.median} : ${isNaN(barData[3][1]) ? '' : barData[3][1]} µg/L
                    ` : ''
                return (isUndefined(barData[0][1]) && isUndefined(barData[1][1])) ? '' :
                    `
                    ${barData[0][0]}<br>
                    ${i18n.nbSumPesticides} : ${countMonthsAnalysis[barData[0][0]]}<br>
                    ${i18n.min} : ${barData[0][1] ?? ''} µg/L<br>
                    ${i18n.max} : ${barData[1][2] ?? ''} µg/L<br>
                    ${additionnalTooltip}
                    `
            },
        },
        toolbox: {
            top: '50',
            right: '35',
            showTitle: false,
            itemSize: '18',
            tooltip: {
                show: true,
                position: 'bottom',
            },
            feature: {
                myToolToggleMarker: {
                    show: featuresVisibility,
                    title: i18n.toggleMarker,
                    icon: chartSymbol,
                    onclick: toggleAdditionnalSerie,
                    iconStyle: {
                        borderColor: additionnalSerie ? greyBlue : grey,
                    },
                },
                myToolFilterStations: {
                    show: featuresVisibility,
                    title: i18n.parametersList,
                    icon: filterIcon,
                    onclick: openPesticidesPopin,
                },
                myToolFullScreen: {
                    show: featuresVisibility,
                    title: i18n.fullScreen,
                    icon: isFullScreen ? fullScreenExitIcon : fullScreenIcon,
                    onclick: () => isFullScreen ? closeFullScreen() : openFullScreen(),
                },
                myToolQuantification: {
                    show: featuresVisibility,
                    title: i18n.displayYAxis0,
                    icon: quantificationIcon,
                    onclick: toggleFixMinScale,
                    iconStyle: {
                        borderColor: fixMinScale ? greyBlue : grey,
                    },
                },
                myZoom: {
                    show: featuresVisibility,
                    title: i18n.zoomVertical,
                    icon: swapVertical,
                    onclick: toggleOrdinatePopin,
                    iconStyle: {
                        borderColor: isOrdinatePopinOpen ? greyBlue : grey,
                    },
                },
                saveAsImage: {
                    show: featuresVisibility,
                    title: i18n.pictureExport,
                    icon: exportPictureIcon,
                    name: i18n.annualQuantificationsDistribution,
                },
                myToolExport: {
                    show: featuresVisibility,
                    title: i18n.excelExport,
                    icon: exportExcelIcon,
                    onclick: () => {
                        const catchmentColumns = isEmpty(catchment) ? {} : {
                            code: catchment.code,
                            catchment: catchment.name,
                        }
                        const exportData = monthsLabel.flatMap(month => {
                            return {
                                ...catchmentColumns,
                                month,
                                monthIndex: monthsLabel.find(m => m.name === month)?.value,
                                min: firstSerie.find(m => m[0] === month)?.[1],
                                max: secondSerie.find(m => m[0] === month)?.[1],
                                average: averageSerie.find(m => m[0] === month)?.[1],
                                median: medianSerie.find(m => m[0] === month)?.[1],
                                nbSumPesticides: countMonthsAnalysis[month] ?? 0,
                            }
                        })
                        const sortedData = orderBy(exportData, ['monthIndex'], ['desc'])
                        exportFile({
                            data: exportData.length ? [
                                {
                                    ...sortedData[0],
                                    headers: isEmpty(catchment) ? EXPORT_RESOURCE_COLUMNS : EXPORT_CATCHMENT_COLUMNS,
                                },
                                ...sortedData.slice(1),
                            ] : [],
                            exportType: 'xlsx',
                            titleFile: i18n.annualQuantificationsDistribution,
                        }, true)
                    },
                },
            },
        },
    }

    return (
        <>
            {!isLoaded && (<ProgressCard progress={progress}/>)}
            {isLoaded && (
                <>
                    <div
                        onMouseOver={() => showFeatures()}
                        onMouseOut={() => hideFeatures()}
                    >
                        <ReactECharts
                            echarts={echarts}
                            option={options}
                            notMerge
                            style={{ height: 400 }}
                        />
                    </div>
                    <SelectionTableModal
                        isOpen={isPesticidePopinOpen}
                        onClose={closePesticidesPopin}
                        onValidate={list => {
                            closePesticidesPopin()
                            setConsideredPesticides(list.length > 0 ? list : usedSumPesticides)
                        }}
                        title={i18n.pesticidesSelection}

                        listData={activeParameters}
                        defaultSelectionList={consideredPesticides}

                        listHeaders={['name', 'type', 'nature', 'status']}
                        listTitle={i18n.nonSelectedParameters}
                        selectionListTitle={i18n.selectedParameters}
                        maxHeightTable={'53vh'}

                        filterField={ParameterFilterField}
                        filterFunction={parameterFilterFunction}
                    />
                    <Dialog
                        fullScreen
                        open={isFullScreen}
                    >
                        <DialogContent>
                            <div
                                onMouseOver={() => showFeatures()}
                                onMouseOut={() => hideFeatures()}
                            >
                                <ReactECharts
                                    echarts={echarts}
                                    option={options}
                                    notMerge
                                    style={{ height: '75vh' }}
                                />
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeFullScreen} variant='contained'>
                                {i18n.close}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog
                        open={isOrdinatePopinOpen}
                    >
                        <DefaultDialogTitle
                            title={i18n.zoomVertical}
                            onClose={() => closeOrdinatePopin()}
                        />
                        <DialogContent>
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <NumberField
                                        title={i18n.min}
                                        value={ordinateFields.min}
                                        onChange={v => setOrdinateFields(prev => ({ ...prev, min: v }))}
                                        floatValue
                                        onEnterKeyPress={v => {
                                            setOrdinateFields(prev => ({ ...prev, min: v }))
                                            setOrdinateValues({ min: v, max: ordinateFields.max })
                                            closeOrdinatePopin()
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <NumberField
                                        title={i18n.max}
                                        value={ordinateFields.max}
                                        onChange={v => setOrdinateFields(prev => ({ ...prev, max: v }))}
                                        floatValue
                                        onEnterKeyPress={v => {
                                            setOrdinateFields(prev => ({ ...prev, max: v }))
                                            setOrdinateValues({ min: ordinateFields.min, max: v })
                                            closeOrdinatePopin()
                                        }}
                                    />
                                </Grid>
                                <Grid size={3} offset={6}>
                                    <Button
                                        onClick={() => setOrdinateFields({ min: null, max: null })}
                                        variant='outlined'
                                        fullWidth
                                    >
                                        {i18n.clean}
                                    </Button>
                                </Grid>
                                <Grid size={3}>
                                    <Button
                                        onClick={() => {
                                            setOrdinateValues({ min: ordinateFields.min, max: ordinateFields.max })
                                            closeOrdinatePopin()
                                        }}
                                        variant='contained'
                                        fullWidth
                                    >
                                        {i18n.apply}
                                    </Button>
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </Dialog>
                </>)}
        </>
    )
}

AnnualDistributionGraph.propTypes = {
    sumPesticidesAnalysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
    stationIds: PropTypes.arrayOf(PropTypes.number),
    startPeriod: PropTypes.number,
    endPeriod: PropTypes.number,
    producerExcluded: PropTypes.arrayOf(PropTypes.number),
}

export default AnnualDistributionGraph